import { useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import {
  PanelBottom,
  Megaphone,
  ScrollText,
  Share2,
  Flag,
  Plus,
  Sparkles,
  Pencil,
  Trash2,
  Star,
  Loader2,
  Library,
} from "lucide-react";

// -----------------------------------------------------------------------------
// Shared asset-type metadata. Exported so the Email Express generator form can
// reuse the same labels/icons/order when attaching assets to a new email.
// -----------------------------------------------------------------------------
export type UniversalAssetType =
  | "footer"
  | "promo_banner"
  | "disclaimer"
  | "social_links"
  | "announcement_bar";

export type UniversalSocialLink = { platform: string; url: string };
export type UniversalAssetContent = Record<string, any>;

export type UniversalAsset = {
  id: string;
  brandId: string;
  type: UniversalAssetType;
  name: string;
  content: UniversalAssetContent;
  isDefault: boolean;
  createdAt?: string | null;
  updatedAt?: string | null;
};

type IconType = React.ComponentType<{ className?: string }>;

export const UNIVERSAL_ASSET_TYPE_LIST: {
  type: UniversalAssetType;
  label: string;
  desc: string;
  icon: IconType;
}[] = [
  { type: "footer", label: "Footer", desc: "Logo, links, unsubscribe & social", icon: PanelBottom },
  { type: "promo_banner", label: "Promo banner", desc: "Image, headline & CTA", icon: Megaphone },
  { type: "disclaimer", label: "Disclaimer", desc: "Legal / compliance text", icon: ScrollText },
  { type: "social_links", label: "Social links", desc: "Your social profiles", icon: Share2 },
  { type: "announcement_bar", label: "Announcement bar", desc: "Top-of-email message", icon: Flag },
];

const META: Record<UniversalAssetType, { label: string; desc: string; icon: IconType }> =
  Object.fromEntries(
    UNIVERSAL_ASSET_TYPE_LIST.map((m) => [m.type, { label: m.label, desc: m.desc, icon: m.icon }]),
  ) as Record<UniversalAssetType, { label: string; desc: string; icon: IconType }>;

type FieldDef = {
  key: string;
  label: string;
  type: "text" | "textarea" | "links";
  placeholder?: string;
};

const ASSET_FIELDS: Record<UniversalAssetType, FieldDef[]> = {
  footer: [
    { key: "companyName", label: "Company name", type: "text" },
    { key: "logoUrl", label: "Logo URL", type: "text", placeholder: "https://…" },
    { key: "address", label: "Mailing address", type: "textarea" },
    { key: "legalText", label: "Legal / copyright line", type: "textarea" },
    { key: "unsubscribeLabel", label: "Unsubscribe label", type: "text" },
    { key: "unsubscribeUrl", label: "Unsubscribe URL", type: "text", placeholder: "https://…" },
    { key: "preferencesLabel", label: "Preferences label", type: "text" },
    { key: "preferencesUrl", label: "Preferences URL", type: "text", placeholder: "https://…" },
    { key: "socialLinks", label: "Social links", type: "links" },
  ],
  promo_banner: [
    { key: "imageUrl", label: "Image URL", type: "text", placeholder: "https://…" },
    { key: "headline", label: "Headline", type: "text" },
    { key: "subtext", label: "Subtext", type: "textarea" },
    { key: "ctaLabel", label: "Button label", type: "text" },
    { key: "linkUrl", label: "Button URL", type: "text", placeholder: "https://…" },
  ],
  disclaimer: [{ key: "text", label: "Disclaimer text", type: "textarea" }],
  social_links: [{ key: "links", label: "Social links", type: "links" }],
  announcement_bar: [
    { key: "text", label: "Announcement text", type: "text" },
    { key: "linkLabel", label: "Link label", type: "text" },
    { key: "linkUrl", label: "Link URL", type: "text", placeholder: "https://…" },
  ],
};

// A short, human-readable summary of an asset's content for its card.
function previewLine(asset: UniversalAsset): string {
  const c = asset.content || {};
  switch (asset.type) {
    case "footer": {
      const social = Array.isArray(c.socialLinks) ? c.socialLinks.length : 0;
      return [c.companyName, social ? `${social} social link${social === 1 ? "" : "s"}` : ""]
        .filter(Boolean)
        .join(" · ") || "Footer block";
    }
    case "promo_banner":
      return c.headline || "Promo banner";
    case "disclaimer":
      return (c.text || "Disclaimer text").slice(0, 120);
    case "social_links": {
      const n = Array.isArray(c.links) ? c.links.length : 0;
      return n ? `${n} social link${n === 1 ? "" : "s"}` : "Social links";
    }
    case "announcement_bar":
      return c.text || "Announcement bar";
    default:
      return "";
  }
}

type EditorDraft = {
  id?: string;
  type: UniversalAssetType;
  name: string;
  content: UniversalAssetContent;
  isDefault: boolean;
};

const ASSETS_KEY = (brandId: string) => [`/api/brands/${brandId}/email-express/assets`];

export function UniversalLibrary({ brandId }: { brandId: string }) {
  const { toast } = useToast();
  const [editing, setEditing] = useState<EditorDraft | null>(null);
  const [pendingGenerateType, setPendingGenerateType] = useState<UniversalAssetType | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<UniversalAsset | null>(null);

  const { data: assets = [], isLoading } = useQuery<UniversalAsset[]>({
    queryKey: ASSETS_KEY(brandId),
    enabled: !!brandId,
  });

  const assetsByType = useMemo(() => {
    const m: Record<string, UniversalAsset[]> = {};
    for (const a of assets) (m[a.type] ||= []).push(a);
    return m;
  }, [assets]);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ASSETS_KEY(brandId) });

  const saveMutation = useMutation({
    mutationFn: async (draft: EditorDraft) => {
      if (draft.id) {
        const res = await apiRequest(
          "PATCH",
          `/api/brands/${brandId}/email-express/assets/${draft.id}`,
          { name: draft.name.trim(), content: draft.content, isDefault: draft.isDefault },
        );
        return res.json();
      }
      const res = await apiRequest("POST", `/api/brands/${brandId}/email-express/assets`, {
        type: draft.type,
        name: draft.name.trim(),
        content: draft.content,
        isDefault: draft.isDefault,
      });
      return res.json();
    },
    onSuccess: (_data, draft) => {
      invalidate();
      setEditing(null);
      toast({ title: draft.id ? "Asset updated" : "Asset saved", description: draft.name.trim() });
    },
    onError: (e: any) =>
      toast({ title: "Save failed", description: e?.message || "Try again", variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest(
        "DELETE",
        `/api/brands/${brandId}/email-express/assets/${id}`,
      );
      return res.json();
    },
    onSuccess: () => {
      invalidate();
      setConfirmDelete(null);
      toast({ title: "Asset deleted" });
    },
    onError: (e: any) =>
      toast({ title: "Delete failed", description: e?.message || "Try again", variant: "destructive" }),
  });

  // Toggle isDefault directly from a card (separate from the editor dialog).
  const defaultMutation = useMutation({
    mutationFn: async ({ id, isDefault }: { id: string; isDefault: boolean }) => {
      const res = await apiRequest(
        "PATCH",
        `/api/brands/${brandId}/email-express/assets/${id}`,
        { isDefault },
      );
      return res.json();
    },
    onSuccess: () => invalidate(),
    onError: (e: any) =>
      toast({ title: "Update failed", description: e?.message || "Try again", variant: "destructive" }),
  });

  // AI-generate fresh content, then open the editor pre-filled with it.
  const generateMutation = useMutation({
    mutationFn: async (type: UniversalAssetType) => {
      const res = await apiRequest(
        "POST",
        `/api/brands/${brandId}/email-express/assets/generate`,
        { type },
      );
      return (await res.json()) as { type: UniversalAssetType; content: UniversalAssetContent };
    },
    onSuccess: (data, type) => {
      setPendingGenerateType(null);
      const count = (assetsByType[type] || []).length;
      setEditing({
        type,
        name: `${META[type].label}${count ? ` ${count + 1}` : ""}`,
        content: data.content || {},
        isDefault: count === 0,
      });
    },
    onError: (e: any) => {
      setPendingGenerateType(null);
      toast({ title: "Generation failed", description: e?.message || "Try again", variant: "destructive" });
    },
  });

  const openNew = (type: UniversalAssetType) => {
    const count = (assetsByType[type] || []).length;
    setEditing({
      type,
      name: `${META[type].label}${count ? ` ${count + 1}` : ""}`,
      content: {},
      isDefault: count === 0,
    });
  };

  return (
    <div className="space-y-5" data-testid="universal-library">
      <div className="flex items-start gap-3">
        <div className="relative h-10 w-10 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 text-primary flex items-center justify-center shrink-0 ring-1 ring-primary/20 shadow-sm">
          <Library className="h-4 w-4" />
        </div>
        <div>
          <h2 className="text-base font-semibold tracking-tight">Universal content</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Save reusable blocks once — with their links and copy baked in — then drop them into any
            email. Set a default to pre-fill new emails automatically.
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-28 w-full" />
          ))}
        </div>
      ) : (
        UNIVERSAL_ASSET_TYPE_LIST.map(({ type, label, desc, icon: Icon }) => {
          const list = assetsByType[type] || [];
          const genPending = generateMutation.isPending && pendingGenerateType === type;
          return (
            <Card key={type} className="p-5 border-border/50 space-y-4" data-testid={`asset-group-${type}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0 ring-1 ring-primary/15">
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold leading-tight flex items-center gap-2">
                      {label}
                      <Badge variant="secondary" className="text-[10px] font-medium">
                        {list.length}
                      </Badge>
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">{desc}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    disabled={genPending}
                    onClick={() => {
                      setPendingGenerateType(type);
                      generateMutation.mutate(type);
                    }}
                    data-testid={`button-generate-asset-${type}`}
                  >
                    {genPending ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Sparkles className="h-3.5 w-3.5" />
                    )}
                    AI generate
                  </Button>
                  <Button
                    size="sm"
                    className="gap-1.5"
                    onClick={() => openNew(type)}
                    data-testid={`button-add-asset-${type}`}
                  >
                    <Plus className="h-3.5 w-3.5" /> Add
                  </Button>
                </div>
              </div>

              {list.length === 0 ? (
                <p
                  className="text-xs text-muted-foreground/70 italic py-2"
                  data-testid={`empty-asset-${type}`}
                >
                  No saved {label.toLowerCase()} yet.
                </p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {list.map((a) => (
                    <div
                      key={a.id}
                      className="group flex items-start gap-2.5 rounded-xl border border-border/60 bg-background/60 p-3 hover:border-primary/30 hover:bg-background transition-colors"
                      data-testid={`asset-card-${a.id}`}
                    >
                      <button
                        type="button"
                        title={a.isDefault ? "Default for new emails" : "Set as default"}
                        onClick={() => defaultMutation.mutate({ id: a.id, isDefault: !a.isDefault })}
                        disabled={defaultMutation.isPending}
                        className={[
                          "mt-0.5 shrink-0 transition-colors",
                          a.isDefault
                            ? "text-amber-500"
                            : "text-muted-foreground/40 hover:text-amber-500",
                        ].join(" ")}
                        data-testid={`button-default-${a.id}`}
                      >
                        <Star className={["h-4 w-4", a.isDefault ? "fill-amber-500" : ""].join(" ")} />
                      </button>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold leading-tight truncate flex items-center gap-1.5">
                          {a.name}
                          {a.isDefault && (
                            <Badge className="text-[9px] px-1.5 py-0 h-4 bg-amber-500/15 text-amber-600 dark:text-amber-400 hover:bg-amber-500/15 border-0">
                              Default
                            </Badge>
                          )}
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">
                          {previewLine(a)}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() =>
                            setEditing({
                              id: a.id,
                              type: a.type,
                              name: a.name,
                              content: { ...(a.content || {}) },
                              isDefault: a.isDefault,
                            })
                          }
                          data-testid={`button-edit-${a.id}`}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-destructive"
                          onClick={() => setConfirmDelete(a)}
                          data-testid={`button-delete-${a.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          );
        })
      )}

      <AssetEditorDialog
        draft={editing}
        onClose={() => setEditing(null)}
        onChange={setEditing}
        onSave={() => editing && saveMutation.mutate(editing)}
        saving={saveMutation.isPending}
        onRegenerate={() => editing && generateMutation.mutate(editing.type)}
        regenerating={generateMutation.isPending}
      />

      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete this asset?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            "{confirmDelete?.name}" will be removed from your library. Emails that already use a
            snapshot of it keep their copy.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)} data-testid="button-cancel-delete">
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => confirmDelete && deleteMutation.mutate(confirmDelete.id)}
              data-testid="button-confirm-delete"
            >
              {deleteMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AssetEditorDialog({
  draft,
  onClose,
  onChange,
  onSave,
  saving,
  onRegenerate,
  regenerating,
}: {
  draft: EditorDraft | null;
  onClose: () => void;
  onChange: (d: EditorDraft) => void;
  onSave: () => void;
  saving: boolean;
  onRegenerate: () => void;
  regenerating: boolean;
}) {
  if (!draft) return null;
  const meta = META[draft.type];
  const fields = ASSET_FIELDS[draft.type];
  const setContent = (key: string, value: any) =>
    onChange({ ...draft, content: { ...draft.content, [key]: value } });

  return (
    <Dialog open={!!draft} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="dialog-asset-editor">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <meta.icon className="h-4 w-4 text-primary" />
            {draft.id ? "Edit" : "New"} {meta.label.toLowerCase()}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="asset-name" className="text-xs font-semibold">
              Name
            </Label>
            <Input
              id="asset-name"
              value={draft.name}
              onChange={(e) => onChange({ ...draft, name: e.target.value })}
              placeholder={`e.g. ${meta.label}`}
              data-testid="input-asset-name"
            />
          </div>

          {fields.map((f) => {
            if (f.type === "links") {
              return (
                <LinksEditor
                  key={f.key}
                  label={f.label}
                  value={Array.isArray(draft.content[f.key]) ? draft.content[f.key] : []}
                  onChange={(links) => setContent(f.key, links)}
                />
              );
            }
            const val = typeof draft.content[f.key] === "string" ? draft.content[f.key] : "";
            return (
              <div key={f.key} className="space-y-1.5">
                <Label htmlFor={`asset-${f.key}`} className="text-xs font-semibold">
                  {f.label}
                </Label>
                {f.type === "textarea" ? (
                  <Textarea
                    id={`asset-${f.key}`}
                    value={val}
                    onChange={(e) => setContent(f.key, e.target.value)}
                    placeholder={f.placeholder}
                    rows={3}
                    className="resize-none"
                    data-testid={`input-asset-${f.key}`}
                  />
                ) : (
                  <Input
                    id={`asset-${f.key}`}
                    value={val}
                    onChange={(e) => setContent(f.key, e.target.value)}
                    placeholder={f.placeholder}
                    data-testid={`input-asset-${f.key}`}
                  />
                )}
              </div>
            );
          })}

          <div className="flex items-center justify-between rounded-lg border border-border/60 bg-muted/30 px-3 py-2.5">
            <div>
              <div className="text-xs font-semibold flex items-center gap-1.5">
                <Star className="h-3.5 w-3.5 text-amber-500" /> Set as default
              </div>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                Pre-selected for new emails of this type.
              </p>
            </div>
            <Switch
              checked={draft.isDefault}
              onCheckedChange={(v) => onChange({ ...draft, isDefault: v })}
              data-testid="switch-asset-default"
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            className="gap-1.5 mr-auto"
            onClick={onRegenerate}
            disabled={regenerating}
            data-testid="button-regenerate-asset"
          >
            {regenerating ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Sparkles className="h-3.5 w-3.5" />
            )}
            Generate with AI
          </Button>
          <Button variant="outline" onClick={onClose} data-testid="button-cancel-asset">
            Cancel
          </Button>
          <Button
            className="gap-1.5"
            onClick={onSave}
            disabled={saving || !draft.name.trim()}
            data-testid="button-save-asset"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {draft.id ? "Save changes" : "Save asset"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LinksEditor({
  label,
  value,
  onChange,
}: {
  label: string;
  value: UniversalSocialLink[];
  onChange: (links: UniversalSocialLink[]) => void;
}) {
  const update = (i: number, patch: Partial<UniversalSocialLink>) =>
    onChange(value.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const remove = (i: number) => onChange(value.filter((_, idx) => idx !== i));
  const add = () => onChange([...value, { platform: "", url: "" }]);

  return (
    <div className="space-y-2">
      <Label className="text-xs font-semibold">{label}</Label>
      <div className="space-y-2">
        {value.map((link, i) => (
          <div key={i} className="flex items-center gap-2" data-testid={`link-row-${i}`}>
            <Input
              value={link.platform}
              onChange={(e) => update(i, { platform: e.target.value })}
              placeholder="Platform"
              className="w-32 shrink-0"
              data-testid={`input-link-platform-${i}`}
            />
            <Input
              value={link.url}
              onChange={(e) => update(i, { url: e.target.value })}
              placeholder="https://…"
              data-testid={`input-link-url-${i}`}
            />
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 shrink-0 text-muted-foreground hover:text-destructive"
              onClick={() => remove(i)}
              data-testid={`button-remove-link-${i}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
      </div>
      <Button
        variant="outline"
        size="sm"
        className="gap-1.5"
        onClick={add}
        data-testid="button-add-link"
      >
        <Plus className="h-3.5 w-3.5" /> Add link
      </Button>
    </div>
  );
}
